import { useCallback } from "react";
import { publishEventToSDK } from "@repo/sdk/publisher";
import * as Sentry from "@sentry/nextjs";
import { mutate } from "swr";
import { setAuthCookie } from "@repo/session/auth-cookie";
import { SUBSCRIPTION_STATUS_KEY } from "src/lib/subscription-gate";

interface PaymentCompleteInput {
  customerId: string;
  subscriptionId: string;
  /** Whether this checkout started a free trial. Known by the Elements flow
   *  (setup_intent = trial); omitted by express/legacy paths (unknown). */
  isTrialing?: boolean;
}

interface PaymentCompleteResult {
  externalAccountId: string;
  customerId: string;
}

/**
 * Shared hook for handling post-payment completion logic.
 *
 * After inline Elements checkout confirms payment (or the express-checkout
 * redirect reconciles a wallet payment — both card and Apple/Google Pay
 * paths funnel through this hook):
 * 1. POST /api/process-payment to update DB paymentId
 * 2. Set auth cookie with externalAccountId + customerId
 * 3. Optimistically update SWR subscription status
 * 4. Publish termsAcceptance + subscriptionCompleted SDK events
 */
export function usePaymentComplete() {
  const handlePaymentComplete = useCallback(
    async ({
      customerId,
      isTrialing,
    }: PaymentCompleteInput): Promise<PaymentCompleteResult> => {
      const res = await fetch("/api/process-payment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customerId }),
      });

      const data = await res.json();

      if (data.error) {
        throw new Error(data.error);
      }

      if (!data.externalAccountId) {
        Sentry.captureException(
          new Error("Missing externalAccountId after payment"),
          { extra: { customerId, data } }
        );
      }

      setAuthCookie({
        externalAccountId: data.externalAccountId,
        customerId: data.customerId,
      });

      // Optimistic subscription state. `isTrialing` must be part of the write
      // when known: the paid-feature gate reads it from this key, and a
      // partial object (isTrialing missing → falsy) silently disabled the
      // gate for the whole post-checkout session. Never revalidate here —
      // express completions race the subscription-creating webhook (a refetch
      // can overwrite this with "no subscription"), and Elements immediate
      // charges can transiently read `incomplete` (→ payment_issue flash).
      // Paths that can't supply isTrialing (express redirect lands on a full
      // page load) get a fresh SWR fetch naturally.
      mutate(
        SUBSCRIPTION_STATUS_KEY,
        {
          subscriptionState: "active",
          hasActiveSubscription: true,
          ...(isTrialing === undefined ? {} : { isTrialing }),
        },
        { revalidate: false }
      );

      // termsAcceptance triggers the Owlet host app to share the full account details
      // TODO: make the termsAcceptance trigger point a Statsig experiment
      publishEventToSDK({ topic: "termsAcceptance", data: "success" });
      publishEventToSDK({ topic: "subscriptionCompleted", data: "success" });

      return {
        externalAccountId: data.externalAccountId,
        customerId: data.customerId,
      };
    },
    []
  );

  return handlePaymentComplete;
}
